import type { JsonApiDoc, JsonApiResource } from 'tio-data/demo/types';
import type { DemoFixtureIdentity } from 'tio-employee/demo/types';
import {
  getDemoData,
  getDemoUserScenario,
  getDemoSessionRole,
  type DemoSessionRoleId,
  type DemoSessionSelection,
} from 'tio-employee/utils/career-pathways-demo-data';

type ResolvedSelection = Required<DemoSessionSelection>;

/**
 * Fabricated `/me` identity document for an active demo session.
 *
 * `SessionContextService.load()` does a raw `fetch(ENV.apiHost + '/me?...')`,
 * then `store.pushPayload(parsed)` + `peekRecord('user', parsed.data.id)`, and
 * derives `this.roles` from `user.uniqueRolesByRelationshipType`. The demo-mode
 * fetch interceptor serves this doc for that request, so the whole session is
 * demo-coherent (greeting, sidebar, role gating, terms gate) without touching
 * the real backend.
 *
 * EVERY UserModel relationship is `async: false`, so every resource referenced
 * by `data` (transitively) MUST appear in `included` or it materializes empty.
 *
 * Role graph (drives `user.computedRoleViews`): the getter iterates
 * `userRole.relationships[].jurisdiction.company` (NOT `userRole.company`)
 * unless `userRole.employee` is set. So the chain is:
 *   user -> user-roles[0] -> relationships[0] -> jurisdiction (a user-role)
 *        -> company -> company-setting
 * The relationship's `relationshipType` is what `applyDemoSession` persists to
 * `localStorage('role')` (`getDemoSessionRole(roleId).relationshipType`). Because
 * this doc yields exactly ONE role view, `sessionContext.currentRole` returns
 * `roles[0]` directly (the single-role fast path), so the picked role and the
 * persisted role agree by construction.
 *
 * STAFF-GATE NOTE (intentional, not resolvable here): `demoMode.isStaffUser`
 * (demo-mode.ts) checks `currentRole.relationshipType` against
 * `STAFF_RELATIONSHIP_TYPES = { 'TAS.Admin.SUPERUSER', 'TAS.Approver.TIO_APPROVER' }`.
 * The three demo session relationshipTypes (PARTICIPANT / TAS.Approver.SUPERVISOR /
 * EMPLOYEE_ADMIN) are NONE of those, so `isStaffUser` is false for every demo
 * role. We deliberately stamp the PERSISTED relationshipType (not a staff type):
 * matching `localStorage('role')` is required for `currentRole` to resolve to the
 * role the user picked. The staff set only gates `isAvailable` when
 * `tioEnvironment === 'production'` AND `demoModeRequireStaffRoleInProd` is on
 * (off by default), so the demo stays active everywhere today. Reconciling the two
 * (so prod-with-the-flag keeps demo active) is a DEMO_SESSION_ROLES /
 * STAFF_RELATIONSHIP_TYPES change, NOT a `/me` doc change — do not "fix" it here.
 *
 * Terms gate (`sessionContext.hasAcceptedAppTerms`) ANDs `usageAgreement`,
 * `privacyAgreement`, and every custom-register agreement; the usage + privacy
 * agreement resources are sideloaded so the demo user is never trapped on the
 * terms screen. (`hasAcceptedAllCustomRegisterTerms` is vacuously true in a demo
 * session because `preRegister.customTerms` is empty — the registration flow that
 * populates it is bypassed — so the custom agreement below is harmless belt-and-
 * suspenders, not load-bearing.)
 */

// --- Stable resource ids ----------------------------------------------------
// User identity is stable across selections so `peekRecord('user', id)` and the
// `findRecord('user', id)` the authenticated route re-issues both resolve.
// Exported so the registry can build the `/users/<id>` fetch-matcher that serves
// this same identity to the route's findRecord BUILDER read (see registry.ts).
export const USER_ID = 'demo-user';
// Exported alongside USER_ID so the registry can build the `/people/<id>` fetch
// matcher that serves the session person to builder findRecord reads (the
// finance/history + loans/detail routes re-load the person via the builder).
export const PERSON_ID = 'demo-person';
const USER_ROLE_ID = 'demo-user-role-1';
const USER_RELATIONSHIP_ID = 'demo-user-relationship-1';
const JURISDICTION_ROLE_ID = 'demo-jurisdiction-role-1';
const PRIMARY_EMAIL_ID = 'demo-email-primary';
const USAGE_AGREEMENT_ID = 'demo-agreement-usage';
const PRIVACY_AGREEMENT_ID = 'demo-agreement-privacy';
const CUSTOM_AGREEMENT_ID = 'demo-agreement-custom-register';
const CUSTOM_CONDITION_ID = 'demo-condition-custom-register';

// Reused from tas-fixtures.ts so the identity employee/company/company-setting
// do not diverge from the TAS dashboard fixtures (same ids => same records).
const EMPLOYEE_ID = 'demo-tas-employee-1';
const COMPANY_ID = 'demo-tas-company-1';
const COMPANY_SETTING_ID = 'demo-tas-companysetting-1';

const PRIMARY_EMAIL_ADDRESS = 'demo.user@example.com';
const DEFAULT_FIRST_NAME = 'Demo';
const DEFAULT_LAST_NAME = 'User';

interface IdentityName {
  firstName: string;
  lastName: string;
}

/**
 * Resolve the demo user's display name. Prefers the per-client
 * `DemoClientData.employee` slot (first/last); else splits the active user
 * scenario's `name` (e.g. "Power User") into first/last; else a static default.
 *
 * NOTE: this name drives the greeting/sidebar via `sessionContext.person`, which
 * resolves to the /me doc's person ONLY when the active scenario registers no
 * `findRecord('person')` matcher (e.g. the TAS product registers only an
 * employee findRecord). A scenario that DOES register a demo person would shadow
 * this name — by design.
 */
// company-setting reused from tas-fixtures, carrying BOTH the tuitionAssistance
// block and the SLR `modules.syf` flag, so currentEmployee.company.companySetting
// is coherent for every product (TAS + contributions) off the one demo identity.
// Shared by the /me identity doc AND the standalone company findRecord doc so the
// two can never drift.
function buildDemoCompanySettingResource(): JsonApiResource {
  return {
    type: 'company-setting',
    id: COMPANY_SETTING_ID,
    attributes: {
      tuitionAssistance: {
        hideAnnualBenefitsCard: false,
        isDependentProgramsEnabled: false,
      },
      modules: { syf: { isEnabled: true } },
      // Eligibility-file cards read companySetting.eligibilityDefault
      // ['auto-process'] UNGUARDED once a companySetting exists.
      eligibilityDefault: { 'auto-process': true },
    },
  };
}

// company id reused from tas-fixtures, name overridden to the per-client demo
// employer so the sidebar/header employer matches the selected client.
function buildDemoCompanyResource(employerName: string): JsonApiResource {
  return {
    type: 'company',
    id: COMPANY_ID,
    attributes: { name: employerName, isActive: true },
    relationships: {
      companySetting: { data: { type: 'company-setting', id: COMPANY_SETTING_ID } },
    },
  };
}

/**
 * Standalone `company` findRecord document for the active demo session, served
 * by a catch-all `findRecord('company')` matcher registered LAST (so a
 * product-specific company fixture, e.g. PSLF's, still wins). The handler
 * echoes the REQUESTED company id onto this doc, so ANY company lookup during a
 * demo — including ids only referenced via a sideloaded relationship (e.g.
 * `demo-slr-company-1`) — resolves to the demo company + company-setting
 * instead of falling through to the real API and 404-ing. This is the
 * defense-in-depth half of the demo-leak fix: an ACTIVE demo session can never
 * 404 on a company.
 */
export function buildDemoCompanyDoc(selection: ResolvedSelection): JsonApiDoc {
  const employerName = getDemoData(selection.clientId).client.name;
  return {
    data: buildDemoCompanyResource(employerName),
    included: [buildDemoCompanySettingResource()],
  };
}

/**
 * The single source of truth for the demo session's display identity. The
 * registry resolves this once per request and threads it through every product
 * builder, so product fixtures (TAS approval queue, contributions person, PSLF
 * borrower/employer) always carry the SAME employee + employer names as the
 * `/me` identity — hardcoded fixture names are how the header said
 * "Lifepoint Health" while the approval queue said "Acme Corporation".
 */
export function resolveDemoFixtureIdentity(selection: ResolvedSelection): DemoFixtureIdentity {
  return {
    ...resolveIdentityName(selection),
    employerName: getDemoData(selection.clientId).client.name,
  };
}

/**
 * Short plan/company code derived from the employer name ("Lifepoint Health"
 * → "LH"). Several admin surfaces concatenate `plan.companyKey` with the plan
 * name, so every demo plan resource must carry it or screens render
 * "undefined - <plan>".
 */
export function demoCompanyKey(employerName: string): string {
  return (
    employerName
      .split(/\s+/)
      .map((word) => word[0] ?? '')
      .join('')
      .toUpperCase()
      .slice(0, 4) || 'DEMO'
  );
}

/**
 * Standalone `employee` findRecord document, served by a catch-all
 * `findRecord('employee')` matcher registered LAST (product employee fixtures
 * — TAS/contributions/PSLF — win first). The handler echoes the requested id,
 * so admin drill-ins into ANY employee row (e.g. the directory in a non-TAS
 * demo) resolve to a coherent demo employee instead of the live API. The
 * shape mirrors the TAS product employee (which the admin show surfaces are
 * verified against), including the unguarded reads: customAttributes,
 * employmentType, and the participant/company graph.
 */
export function buildDemoEmployeeCatchAllDoc(selection: ResolvedSelection): JsonApiDoc {
  const employerName = getDemoData(selection.clientId).client.name;
  // STRUCTURAL attributes only — no names/email/payroll. The handler echoes
  // the requested id and store.push MERGES attributes, so a doc carrying
  // identity names would rename whichever employee row (directory colleague,
  // queue applicant) the admin drilled into. Rows reached from a list have
  // their display attributes pushed by that list already; this doc just
  // guarantees the unguarded structural reads (customAttributes,
  // employmentType, participant/company/plan graph) resolve.
  return {
    data: {
      type: 'employee',
      id: EMPLOYEE_ID,
      attributes: {
        participationStatus: 'participating',
        isActive: true,
        customAttributes: {},
        employmentType: 'FULL_TIME',
        eligibilityStartDate: '2022-02-01',
        createdAt: '2022-01-15T09:00:00Z',
        statistics: { user: { total_accounts: 1, total_loans: 2 } },
      },
      relationships: {
        company: { data: { type: 'company', id: COMPANY_ID } },
        tasParticipant: { data: { type: 'tas-participant', id: 'demo-tas-participant-1' } },
        plan: { data: { type: 'plan', id: 'demo-slr-plan-1' } },
      },
    },
    included: [
      buildDemoCompanyResource(employerName),
      buildDemoCompanySettingResource(),
      {
        type: 'tas-participant',
        id: 'demo-tas-participant-1',
        attributes: { state: 'TAS.ParticipantState.ACTIVE', employmentStartDate: '2022-01-15' },
        relationships: { tasEligibilities: { data: [] }, tasGroupings: { data: [] } },
      },
      {
        type: 'plan',
        id: 'demo-slr-plan-1',
        attributes: {
          name: `${employerName} Student Loan Repayment`,
          companyKey: demoCompanyKey(employerName),
        },
      },
    ],
  };
}

/**
 * Standalone `person` findRecord document, served by a catch-all
 * `findRecord('person')` matcher registered LAST (product person fixtures —
 * contributions/PSLF — still win via first-match-wins). The handler echoes the
 * requested id, so any person lookup during a demo resolves to the session
 * identity (same names as the /me doc) with no linked accounts, instead of
 * falling through to the real API.
 */
export function buildDemoPersonDoc(selection: ResolvedSelection): JsonApiDoc {
  const { firstName, lastName } = resolveIdentityName(selection);
  return {
    data: {
      type: 'person',
      id: PERSON_ID,
      attributes: { firstName, lastName, preferences: {} },
      relationships: { accounts: { data: [] } },
    },
  };
}

/**
 * Display identities for the non-participant session roles. The user-scenario
 * personas (First Login, Power User, …) are PARTICIPANT-only employee journeys,
 * and the per-client `employee` slot is the client's flagship employee story —
 * deriving a supervisor/admin name from either renders the session user
 * literally named "Power User" in the header, `/me` identity, and admin
 * directory. These roles resolve to fixed believable names instead.
 */
const ROLE_IDENTITY_NAMES: Partial<Record<DemoSessionRoleId, IdentityName>> = {
  supervisor: { firstName: 'Morgan', lastName: 'Reyes' },
  admin: { firstName: 'Casey', lastName: 'Whitman' },
};

function resolveIdentityName(selection: ResolvedSelection): IdentityName {
  const roleIdentity = ROLE_IDENTITY_NAMES[selection.roleId];
  if (roleIdentity) {
    return roleIdentity;
  }

  const clientEmployee = getDemoData(selection.clientId).employee;
  if (clientEmployee?.firstName && clientEmployee?.lastName) {
    return { firstName: clientEmployee.firstName, lastName: clientEmployee.lastName };
  }

  const scenarioName = getDemoUserScenario(selection.userScenarioId).name?.trim();
  if (scenarioName) {
    const parts = scenarioName.split(/\s+/);
    const firstName = parts[0] ?? DEFAULT_FIRST_NAME;
    const lastName = parts.length > 1 ? parts.slice(1).join(' ') : DEFAULT_LAST_NAME;
    return { firstName, lastName };
  }

  return { firstName: DEFAULT_FIRST_NAME, lastName: DEFAULT_LAST_NAME };
}

/**
 * Build the fabricated `/me` JSON:API document for the active demo session.
 *
 * @param selection the active demo selection; `selection.roleId` decides the
 *   persisted `relationshipType` (PARTICIPANT / TAS.Approver.SUPERVISOR /
 *   EMPLOYEE_ADMIN) so the relationship and the localStorage role agree.
 */
/**
 * Dasherize a top-level key (`isActive` → `is-active`, `companySetting` →
 * `company-setting`). Attribute VALUES are untouched (only keys).
 */
function dasherizeKey(key: string): string {
  return key.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

function toRawApiResource(resource: JsonApiResource): JsonApiResource {
  const raw: JsonApiResource = { type: resource.type, id: resource.id };
  if (resource.attributes) {
    raw.attributes = Object.fromEntries(
      Object.entries(resource.attributes).map(([key, value]) => [dasherizeKey(key), value])
    );
  }
  if (resource.relationships) {
    raw.relationships = Object.fromEntries(
      Object.entries(resource.relationships).map(([key, value]) => [dasherizeKey(key), value])
    );
  }
  return raw;
}

/**
 * Dasherize a whole camelCase demo doc (data + included) into the raw-API key
 * convention the response/serializer pipeline expects when a fixture is served
 * at the FETCH seam (the `window.fetch` interceptor). A builder read served this
 * way flows through the normal serializer + cache materialization exactly like a
 * live response — so `content.data` materializes as the proper model. (The
 * request-HANDLER op-matchers consume the camelCase docs directly via
 * `store.push` and do NOT use this.)
 */
export function toRawDoc(doc: JsonApiDoc): JsonApiDoc {
  const data = Array.isArray(doc.data)
    ? doc.data.map(toRawApiResource)
    : doc.data
      ? toRawApiResource(doc.data)
      : null;
  return {
    data,
    included: (doc.included ?? []).map(toRawApiResource),
    ...(doc.meta ? { meta: doc.meta } : {}),
  };
}

/**
 * The `/me` raw-fetch variant. `SessionContextService.load()` serves this through
 * the response pipeline, which DASHERIZES keys (it expects raw-API `is-active` /
 * `company-setting`, not camelCase). The camelCase {@link buildDemoIdentityDoc}
 * is for the `findRecord('user')` handler matcher (`store.push`,
 * already-normalized). Same data, two key conventions.
 */
export function buildDemoIdentityRawDoc(selection: ResolvedSelection): JsonApiDoc {
  return toRawDoc(buildDemoIdentityDoc(selection));
}

export function buildDemoIdentityDoc(selection: ResolvedSelection): JsonApiDoc {
  const { firstName, lastName } = resolveIdentityName(selection);
  const employerName = getDemoData(selection.clientId).client.name;
  const relationshipType = getDemoSessionRole(selection.roleId).relationshipType;

  const personResource: JsonApiResource = {
    type: 'person',
    id: PERSON_ID,
    attributes: {
      firstName,
      lastName,
      preferences: {},
    },
    relationships: {
      user: { data: { type: 'user', id: USER_ID } },
      employees: { data: [{ type: 'employee', id: EMPLOYEE_ID }] },
    },
  };

  // Shared with the standalone company findRecord doc (buildDemoCompanyDoc) so
  // the sideloaded identity company and a fresh company lookup never diverge.
  const companySettingResource = buildDemoCompanySettingResource();
  const companyResource = buildDemoCompanyResource(employerName);

  // The demo identity employee carries the union of product relationships
  // (plan + tasParticipant) so `sessionContext.currentEmployee` is coherent for
  // EVERY product directly — without it, SLR/contributions consumers that read
  // currentEmployee.plan.cadence (no findRecord re-fetch, unlike TAS/PSLF) would
  // see an empty plan. Ids are reused from the product fixtures.
  // Same name derivation as the contributions product's plan fixture (which
  // shares this id), so the plan name cannot drift between the two pushes.
  const planResource: JsonApiResource = {
    type: 'plan',
    id: 'demo-slr-plan-1',
    attributes: {
      name: `${employerName} Student Loan Repayment`,
      companyKey: demoCompanyKey(employerName),
      cadence: 'MONTHLY',
      annualLimit: 150000,
      lifetimeLimit: 1200000,
      currency: 'USD',
      // Inline tier schedule (cents; $125/mo matches the contributions
      // product's payment fixtures). Live plans get tiers via the
      // /plans/:id/tiers request, which demo sessions never make —
      // scheduledMonthlyContribution reads this attribute instead.
      tiers: [{ fromCycle: 1, amount: 12500, per: 'MONTHLY' }],
    },
  };

  const tasParticipantResource: JsonApiResource = {
    type: 'tas-participant',
    id: 'demo-tas-participant-1',
    attributes: { state: 'TAS.ParticipantState.ACTIVE', employmentStartDate: '2022-01-15' },
    relationships: { tasEligibilities: { data: [] }, tasGroupings: { data: [] } },
  };

  const employeeResource: JsonApiResource = {
    type: 'employee',
    id: EMPLOYEE_ID,
    attributes: { firstName, lastName, isActive: true },
    relationships: {
      company: { data: { type: 'company', id: COMPANY_ID } },
      person: { data: { type: 'person', id: PERSON_ID } },
      plan: { data: { type: 'plan', id: 'demo-slr-plan-1' } },
      tasParticipant: { data: { type: 'tas-participant', id: 'demo-tas-participant-1' } },
    },
  };

  // The jurisdiction is itself a user-role record (user-relationship.jurisdiction
  // is belongsTo('user-role')). Its `company` is what computedRoleViews reads;
  // its own `roleType` is never read by the getter (cosmetic).
  const jurisdictionRoleResource: JsonApiResource = {
    type: 'user-role',
    id: JURISDICTION_ROLE_ID,
    attributes: { roleType: relationshipType, isActive: true },
    relationships: {
      company: { data: { type: 'company', id: COMPANY_ID } },
    },
  };

  const userRelationshipResource: JsonApiResource = {
    type: 'user-relationship',
    id: USER_RELATIONSHIP_ID,
    attributes: { relationshipType, isActive: true },
    relationships: {
      role: { data: { type: 'user-role', id: USER_ROLE_ID } },
      jurisdiction: { data: { type: 'user-role', id: JURISDICTION_ROLE_ID } },
    },
  };

  const userRoleResource: JsonApiResource = {
    type: 'user-role',
    id: USER_ROLE_ID,
    attributes: { roleType: relationshipType, isActive: true },
    // No `user` back-link: user-role declares belongsTo('user', inverse: 'roles')
    // but the user model's hasMany is `userRoles` (inverse: 'user'), so a back-link
    // here breaks inverse resolution. The forward user.userRoles link is enough
    // for computedRoleViews.
    relationships: {
      relationships: {
        data: [{ type: 'user-relationship', id: USER_RELATIONSHIP_ID }],
      },
    },
  };

  const usageAgreementResource: JsonApiResource = {
    type: 'agreement',
    id: USAGE_AGREEMENT_ID,
    attributes: { classification: 'app_terms_of_use', isExpired: false },
  };

  const privacyAgreementResource: JsonApiResource = {
    type: 'agreement',
    id: PRIVACY_AGREEMENT_ID,
    attributes: { classification: 'app_privacy_policy', isExpired: false },
  };

  const customConditionResource: JsonApiResource = {
    type: 'condition',
    id: CUSTOM_CONDITION_ID,
    attributes: { classification: 'custom' },
  };

  const customAgreementResource: JsonApiResource = {
    type: 'agreement',
    id: CUSTOM_AGREEMENT_ID,
    attributes: { classification: 'custom', isExpired: false },
    relationships: {
      condition: { data: { type: 'condition', id: CUSTOM_CONDITION_ID } },
    },
  };

  const primaryEmailResource: JsonApiResource = {
    type: 'email-address',
    id: PRIMARY_EMAIL_ID,
    attributes: { email: PRIMARY_EMAIL_ADDRESS, contactType: 'PRIMARY', isActive: true },
  };

  const userResource: JsonApiResource = {
    type: 'user',
    id: USER_ID,
    attributes: { isActive: true },
    relationships: {
      person: { data: { type: 'person', id: PERSON_ID } },
      employees: { data: [{ type: 'employee', id: EMPLOYEE_ID }] },
      userRoles: { data: [{ type: 'user-role', id: USER_ROLE_ID }] },
      usageAgreement: { data: { type: 'agreement', id: USAGE_AGREEMENT_ID } },
      privacyAgreement: { data: { type: 'agreement', id: PRIVACY_AGREEMENT_ID } },
      customRegisterAgreements: {
        data: [{ type: 'agreement', id: CUSTOM_AGREEMENT_ID }],
      },
      emailAddresses: { data: [{ type: 'email-address', id: PRIMARY_EMAIL_ID }] },
    },
  };

  return {
    data: userResource,
    included: [
      personResource,
      employeeResource,
      planResource,
      tasParticipantResource,
      companyResource,
      companySettingResource,
      userRoleResource,
      userRelationshipResource,
      jurisdictionRoleResource,
      usageAgreementResource,
      privacyAgreementResource,
      customAgreementResource,
      customConditionResource,
      primaryEmailResource,
    ],
  };
}
